<!--
    Top-bar live ticker tape.

    One continuously-scrolling marquee that merges Updates Required,
    Activity, and unread Messages into a single horizontal stream.

    Layout:
      .tlf-tape
        .tlf-tape__edge--left      ← "LIVE" label + pulsing dot
        .tlf-tape__viewport
          .tlf-tape__track         ← horizontally scrolls (CSS animation)
            [items repeated TWICE so the loop is seamless]
        .tlf-tape__edge--right     ← mirrored "LIVE" label + pulsing dot

    Both edges show the same LIVE indicator so the tape reads as
    "live on both ends" — keeps the user's eye anchored regardless
    of which side they're glancing at, and visually frames the
    scrolling content between two matching markers.

    Click anywhere on the tape to pause (via CSS :hover); click an
    item to open its source surface.
-->
<div class="tlf-tape" [class.tlf-tape--compact]="compact">
    <!-- Left edge — anchored category label that doubles as the
         "live" indicator. Stays put while the track scrolls behind
         the soft fade-out. -->
    <div class="tlf-tape__edge tlf-tape__edge--left">
        <span class="tlf-tape__live">
            <span class="tlf-tape__live-dot"></span>
            Live
        </span>
    </div>

    <!-- Viewport — clips the overflowing track and supplies a
         soft fade-out at the right edge so items dissolve in/out
         instead of hard-clipping at the boundary. -->
    <div class="tlf-tape__viewport">
        <!--
            The track is rendered TWICE: the second copy is an
            aria-hidden duplicate of the first. The CSS animation
            translates the track from translateX(0) to
            translateX(-50%), so when the first copy has fully
            scrolled out of view the second copy is in the same
            position the first started in — looping seamlessly
            without any JS animation loop.
        -->
        <div class="tlf-tape__track" #trackEl>
            <ng-container *ngFor="let item of streamItems; trackBy: trackById">
                <a
                    class="tlf-tape__item"
                    [class.tlf-tape__item--success]="item.accent === 'success'"
                    [class.tlf-tape__item--info]="item.accent === 'info'"
                    [class.tlf-tape__item--warning]="item.accent === 'warning'"
                    [class.tlf-tape__item--danger]="item.accent === 'danger'"
                    [class.tlf-tape__item--neutral]="item.accent === 'neutral'"
                    [attr.data-id]="item.id"
                    (click)="onItemClick(item)"
                    [matTooltip]="item.title + (item.caption ? ' · ' + item.caption : '')"
                    matTooltipPosition="below"
                    tabindex="0"
                    role="button"
                >
                    <span class="tlf-tape__dot">
                        <mat-icon>{{ item.icon }}</mat-icon>
                    </span>
                    <span *ngIf="item.badge" class="tlf-tape__badge">{{ item.badge }}</span>
                    <span class="tlf-tape__name">{{ item.title }}</span>
                    <span *ngIf="item.caption" class="tlf-tape__caption">{{ item.caption }}</span>
                    <span *ngIf="item.meta" class="tlf-tape__meta">{{ item.meta }}</span>
                </a>
                <span class="tlf-tape__sep" aria-hidden="true">·</span>
            </ng-container>

            <!-- Duplicate copy for the seamless loop. The dupes are
                 inert (aria-hidden) so screen readers don't see the
                 same row twice. -->
            <ng-container *ngFor="let item of streamItems; trackBy: trackById" aria-hidden="true">
                <a
                    class="tlf-tape__item"
                    [class.tlf-tape__item--success]="item.accent === 'success'"
                    [class.tlf-tape__item--info]="item.accent === 'info'"
                    [class.tlf-tape__item--warning]="item.accent === 'warning'"
                    [class.tlf-tape__item--danger]="item.accent === 'danger'"
                    [class.tlf-tape__item--neutral]="item.accent === 'neutral'"
                    [attr.data-id]="item.id"
                    (click)="onItemClick(item)"
                    aria-hidden="true"
                    tabindex="-1"
                >
                    <span class="tlf-tape__dot">
                        <mat-icon>{{ item.icon }}</mat-icon>
                    </span>
                    <span *ngIf="item.badge" class="tlf-tape__badge">{{ item.badge }}</span>
                    <span class="tlf-tape__name">{{ item.title }}</span>
                    <span *ngIf="item.caption" class="tlf-tape__caption">{{ item.caption }}</span>
                    <span *ngIf="item.meta" class="tlf-tape__meta">{{ item.meta }}</span>
                </a>
                <span class="tlf-tape__sep" aria-hidden="true">·</span>
            </ng-container>
        </div>
    </div>

    <!-- Right edge — mirrors the left "LIVE" badge so the tape
         is framed identically on both sides. Purely decorative on
         this side (the left one is the canonical screen-reader
         label) so it's hidden from assistive tech. -->
    <div class="tlf-tape__edge tlf-tape__edge--right" aria-hidden="true">
        <span class="tlf-tape__live">
            <span class="tlf-tape__live-dot"></span>
            Live
        </span>
    </div>
</div>
